import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Menu, X, User, LogOut, Shield, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MatchReminderBell } from "./MatchReminderBell";
import { MatchReminderBanner } from "./MatchReminderBanner";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: "/news", label: "News" },
    { path: "/fixtures", label: "Fixtures" },
    { path: "/tables", label: "Tables" },
    { path: "/forum", label: "Forum" },
    { path: "/fan-memories", label: "Fan Memories" },
    { path: "/fan-clubs", label: "Fan Clubs" },
    { path: "/shop", label: "Shop" },
    { path: "/on-this-day", label: "On This Day" },
    { path: "/chants", label: "West Ham Chants" },
    { path: "/west-ham-memes", label: "Memes" },
    { path: "/transfers", label: "Transfers" },
    { path: "/stats", label: "Stats" },
    { path: "/players", label: "Players" },
    { path: "/history", label: "History" },
    { path: "/cups", label: "Cups" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
    <nav className="bg-[#7C2C3B] sticky top-0 z-50 shadow-lg" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <span className="text-[#7C2C3B] font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>ALL</span>
            </div>
            <span className="text-white font-bold text-xl tracking-tight hidden sm:block" style={{ fontFamily: 'Barlow Condensed' }}>
              ALL WHUFC
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-1.5 xl:px-2.5 py-2 text-xs font-medium transition-colors whitespace-nowrap ${
                  isActive(link.path)
                    ? "text-[#F8D742]"
                    : "text-white hover:text-[#2BAEE5]"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label.toUpperCase()}
              </Link>
            ))}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            {user && <MatchReminderBell />}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 text-white hover:text-[#2BAEE5] transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    {user.profile_image_url ? (
                      <img src={user.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#2BAEE5] flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{user.full_name.split(' ')[0]}</span>
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2" data-testid="nav-profile">
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin">
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-red-600" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="text-white hover:text-[#2BAEE5] text-sm font-medium transition-colors"
                  data-testid="nav-login"
                >
                  LOGIN
                </Link>
                <Link
                  to="/register"
                  className="bg-[#F8D742] text-[#0F172A] px-4 py-2 rounded text-sm font-bold hover:bg-yellow-300 transition-colors"
                  data-testid="nav-subscribe"
                >
                  JOIN NOW
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#63232F] py-2 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
            <div className="grid grid-cols-2 gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-3 py-2.5 text-xs font-medium text-center rounded ${
                    isActive(link.path)
                      ? "text-[#F8D742] bg-[#63232F]"
                      : "text-white hover:bg-[#63232F]"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label.toUpperCase()}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
    <MatchReminderBanner />
    </>
  );
};

export default Navbar;
